import type { Contact, Deal } from '@paintscout/api'
import type { DashboardEstimator, LeadSource } from '@paintscout/util/builder'
import { getLeadSources, uuid, updateDealOwner } from '@paintscout/util/builder'
import type { DropdownSelectOption } from '@paintscout/ui'
import isNull from 'lodash/isNull'
import omitBy from 'lodash/omitBy'
import type { OptionsDocument } from 'paintscout'
import type React from 'react'
import { useCallback, useState } from 'react'

// Handle use/updating of deal values for DealForm
export const useDealValues = ({
  deal,
  options,
  isEditing
}: {
  deal: Partial<Deal>
  options: OptionsDocument
  isEditing: boolean
}): {
  dealValues: Deal
  setDealValues: (deal: Deal) => void
  dealSources: LeadSource[]
  handleSourceChange: (option: DropdownSelectOption) => void
  handleFieldChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleDropdownChange: (args: { id: string; value: string }) => void
  mapAddressToValues: (results: any) => void
  handleContactChange: (contact: Contact) => void
  handleOwnerChange: (owner: DashboardEstimator) => void
  setFieldValue: (args: { field: string; value: string }) => void
} => {
  const [values, setValues] = useState<Deal>(getValues({ deal }))

  const setFieldValue = useCallback((args: { field: string; value: string }) => {
    const { field, value } = args
    setValues((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleFieldChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = event.target
    setValues((prev) => ({ ...prev, [id]: value }))
  }, [])

  const handleDropdownChange = useCallback((args: { id: string; value: string }) => {
    const { id, value } = args
    const [first, second] = id.split('.')
    if (second) {
      setValues((prev) => ({
        ...prev,
        [first]: {
          ...prev[first],
          [second]: value
        }
      }))
    } else {
      setValues((prev) => ({ ...prev, [id]: value }))
    }
  }, [])

  const handleSourceChange = useCallback((option: DropdownSelectOption) => {
    if (isNull(option) || !option) {
      setValues((prev) => ({ ...prev, lead_source: null }))
    } else {
      setValues((prev) => ({ ...prev, lead_source: option.value }))
    }
  }, [])

  const dealSources = getLeadSources({ options, type: 'leadSources' }).map((source) => {
    return {
      value: source.label,
      label: source.label,
      name: source.name
    }
  })

  const handleOwnerChange = useCallback((owner: DashboardEstimator) => {
    setValues((prev) => ({
      ...updateDealOwner({ deal: prev, newOwner: owner })
    }))
  }, [])

  const handleContactChange = useCallback((contact: Contact) => {
    const {
      _id: id,
      first_name,
      last_name,
      email,
      phone_number,
      address,
      city,
      province,
      postal,
      lead_source,
      preferred_contact_method,
      company,
      companyDocId
    } = contact

    setValues((prev) => {
      return {
        ...prev,
        contact: {
          ...prev.contact,
          id: id ?? prev.contact.id,
          firstName: first_name ?? prev.contact.firstName,
          lastName: last_name ?? prev.contact.lastName,
          email: email ?? prev.contact.email,
          phone_number: phone_number ?? prev.contact.phoneNumber,
          preferredContactMethod: preferred_contact_method ?? prev.contact.preferredContactMethod,
          company: company ?? prev.contact.company,
          companyId: companyDocId ?? prev.contact.companyId
        },
        jobAddress: {
          ...prev.jobAddress,
          city: isEditing ? prev.jobAddress.city ?? '' : city ?? '',
          postal: isEditing ? prev.jobAddress.postal ?? '' : postal ?? '',
          province: isEditing ? prev.jobAddress.province ?? '' : province ?? '',
          address: isEditing ? prev.jobAddress.address ?? '' : address ?? ''
        },
        lead_source: isEditing ? prev.lead_source ?? '' : lead_source ?? ''
      }
    })
  }, [])

  const mapAddressToValues = useCallback((results: any) => {
    // map searched address results to values on selection
    const { municipality, postalCode, region, address } = results
    setValues((prev) => {
      return {
        ...prev,
        jobAddress: {
          ...prev.jobAddress,
          city: municipality ?? prev.jobAddress.city,
          postal: postalCode ?? prev.jobAddress.postal,
          province: region ?? prev.jobAddress.province,
          address: address ?? prev.jobAddress.address
        }
      }
    })
  }, [])

  // kill this for now as it was clearing the contact when i created a new contact
  // useEffect(() => {
  //   console.log('useEffect: deal')
  //   console.log(deal)
  //   setValues(getValues(deal))
  // }, [deal])

  return {
    dealValues: values,
    setDealValues: setValues,
    dealSources,
    handleDropdownChange,
    handleFieldChange,
    mapAddressToValues,
    handleSourceChange,
    handleContactChange,
    handleOwnerChange,
    setFieldValue
  }
}

function getValues(args: { deal?: Partial<Deal> }): Deal {
  const { deal = {} } = args
  const sanitized = omitBy(deal, isNull)

  return {
    _id: uuid(),
    _rev: null,
    _deleted: null,
    archived: false,
    name: '',
    jobAddress: {
      address: '',
      city: '',
      postal: '',
      province: ''
    },
    updated: 0,
    contact: {
      id: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: ''
      // address: '',
      // city: '',
      // province: '',
      // postal: '',
      // company: '',
      // lead_source: '',
      // source_id: '',
      // source: '',
      // source_user_id: ''
    },
    owner: null,
    pipeline: {
      value: '',
      label: ''
    },
    stage: {
      value: ''
    },
    lead_source: '',
    ...sanitized,
    // Explicitly preserve files from the original deal even if null — omitBy strips null values,
    // which would cause files to be absent from the submitted deal and overwrite CouchDB files.
    ...('files' in deal ? { files: deal.files } : {}),
    type: 'deal'
  }
}
